import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { adminApi } from '../../api';
import { PageHeader, LoadingState, ErrorState, EmptyState } from '../../components/common';
import { getErrorMessage } from '../../api/client';

import { Modal } from '../../components/Modal';

type Tab = 'integrations' | 'backups' | 'gdpr' | 'notifications' | 'flags' | 'keys-webhooks';

/**
 * FR-ADMIN-03 / 04 / 05 / 06 — Admin platform features.
 * One consolidated page with tabs for each subsystem.
 */
export function AdminPlatformPage() {
  const [tab, setTab] = useState<Tab>('integrations');

  const TABS: Array<{ key: Tab; label: string }> = [
    { key: 'integrations',  label: 'Integrations' },
    { key: 'backups',       label: 'Backups' },
    { key: 'gdpr',          label: 'GDPR' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'flags',         label: 'Feature flags' },
    { key: 'keys-webhooks', label: 'API & Webhooks' },
  ];

  return (
    <div className="px-8 py-10 max-w-[96rem] mx-auto">
      <PageHeader
        eyebrow="Admin · Platform"
        title="Platform administration"
        subtitle="Third-party integrations, backups, GDPR data requests, notification templates, feature flags, API keys and webhooks."
      />

      <div className="card p-1.5 mb-6 inline-flex gap-1 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 text-sm font-semibold rounded-pill transition-all whitespace-nowrap ${
              tab === t.key
                ? 'bg-canvas-subtle text-ink-900'
                : 'text-ink-600 hover:bg-canvas-subtle'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'integrations' && <IntegrationsTab />}
      {tab === 'backups' && <BackupsTab />}
      {tab === 'gdpr' && <GdprTab />}
      {tab === 'notifications' && <NotificationsTab />}
      {tab === 'flags' && <FlagsTab />}
      {tab === 'keys-webhooks' && <KeysWebhooksTab />}
    </div>
  );
}

// ============================================================
// Integrations tab
// ============================================================
function IntegrationsTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [deletingId, setDeletingId] = useState<{ id: string; label: string } | null>(null);

  const q = useQuery({
    queryKey: ['admin-integrations'],
    queryFn: () => adminApi.listIntegrations(),
  });

  const testMut = useMutation({
    mutationFn: ({ provider, id }: { provider: string; id: string }) =>
      adminApi.testIntegration(provider, { integrationId: id }),
    onSuccess: (d: any) => {
      toast[d.ok ? 'success' : 'error'](d.ok ? `Test OK: ${d.details}` : `Test failed: ${d.error}`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['admin-integrations'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => adminApi.deleteIntegration(id),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['admin-integrations'] });
      setDeletingId(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kpis = (() => {
    const rows = q.data ?? [];
    let active = 0, error = 0, inactive = 0;
    for (const i of rows) {
      if (i.status === 'active') active++;
      else if (i.status === 'error') error++;
      else inactive++;
    }
    return { total: rows.length, active, error, inactive };
  })();

  return (
    <div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Configured" value={kpis.total} />
        <KpiTile label="Active" value={kpis.active} accent={kpis.active > 0 ? 'sage' : 'default'} />
        <KpiTile label="Errors" value={kpis.error} accent={kpis.error > 0 ? 'danger' : 'default'} />
        <KpiTile label="Inactive" value={kpis.inactive} />
      </div>

      <div className="flex justify-end mb-4">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New integration</button>
      </div>

      {q.isLoading ? <div className="card"><LoadingState rows={4} /></div> :
        q.error ? <ErrorState error={q.error} /> :
        !q.data?.length ? (
          <div className="card">
            <EmptyState
              message="No integrations configured"
              hint="Connect to third-party services like shipping carriers, payment gateways, or analytics."
              action={
                <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New integration</button>
              }
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Last tested</th>
                    <th>Result</th>
                    <th className="text-right w-32"></th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data ?? []).map((i: any) => (
                    <tr key={i.id}>
                      <td className="font-mono text-xs font-semibold text-ink-900">{i.provider}</td>
                      <td className="text-sm text-ink-900">{i.name}</td>
                      <td><StatusBadge status={i.status} /></td>
                      <td className="text-2xs text-ink-500 font-mono tabular-nums">
                        {i.last_tested_at
                          ? new Date(i.last_tested_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                          : <span className="text-ink-400">—</span>}
                      </td>
                      <td className="text-xs">
                        {i.last_test_error ? (
                          <span className="text-danger" title={i.last_test_error}>
                            {i.last_test_error.slice(0, 40)}{i.last_test_error.length > 40 ? '…' : ''}
                          </span>
                        ) : i.last_test_result === 'ok' ? (
                          <span className="text-sage-700 font-semibold">✓ OK</span>
                        ) : (
                          <span className="text-ink-400">—</span>
                        )}
                      </td>
                      <td className="text-right">
                        <div className="flex gap-1 justify-end">
                          <button
                            className="text-xs font-semibold text-sage-700 hover:bg-sage-50 px-2 py-1 rounded transition-colors"
                            disabled={testMut.isPending}
                            onClick={() => testMut.mutate({ provider: i.provider, id: i.id })}
                          >
                            Test
                          </button>
                          <button
                            className="text-xs font-semibold text-danger hover:bg-red-50 px-2 py-1 rounded transition-colors"
                            onClick={() => setDeletingId({ id: i.id, label: `${i.provider}:${i.name}` })}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {showCreate && <CreateIntegrationModal
        onClose={() => setShowCreate(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['admin-integrations'] }); setShowCreate(false); }} />}

      {deletingId && (
        <ConfirmDialog
          eyebrow="Delete · Integration"
          title="Delete integration?"
          body={<>Delete <span className="font-mono font-semibold">{deletingId.label}</span>? Credentials will be removed and any features depending on this integration will stop working.</>}
          confirmLabel="Delete"
          loading={deleteMut.isPending}
          onClose={() => setDeletingId(null)}
          onConfirm={() => deleteMut.mutate(deletingId.id)}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const cls = {
    active:   'bg-sage-50 text-sage-700 ring-sage-200',
    inactive: 'bg-canvas-subtle text-ink-500 ring-ink-200',
    error:    'bg-red-50 text-red-700 ring-red-200',
    testing:  'bg-blue-50 text-blue-700 ring-blue-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}

function CreateIntegrationModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [provider, setProvider] = useState('stripe');
  const [name, setName] = useState('');
  const [credentials, setCredentials] = useState('{}');

  const mut = useMutation({
    mutationFn: () => {
      let creds;
      try { creds = JSON.parse(credentials); } catch { throw new Error('Credentials must be valid JSON'); }
      return adminApi.createIntegration({ provider, name, credentials: creds });
    },
    onSuccess: () => { toast.success('Created — use Test to validate'); onCreated(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const providerHints: Record<string, string> = {
    stripe: '{"api_key": "sk_test_..."}',
    sendgrid: '{"api_key": "SG...."}',
    twilio: '{"account_sid": "AC...", "auth_token": "..."}',
    slack: '{"webhook_url": "https://hooks.slack.com/..."}',
    s3: '{"access_key_id": "...", "secret_access_key": "...", "bucket": "..."}',
    avalara: '{"account_id": "...", "license_key": "..."}',
    mapbox: '{"access_token": "pk...."}',
  };

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Admin · Integration</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New integration</h2>
        <p className="text-sm text-ink-500 mt-1">
          Add credentials for a 3rd-party service. Use the test action afterwards to confirm connectivity.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Provider</label>
          <select className="input font-mono" value={provider} onChange={(e) => setProvider(e.target.value)}>
            <option value="stripe">stripe</option>
            <option value="sendgrid">sendgrid</option>
            <option value="twilio">twilio</option>
            <option value="slack">slack</option>
            <option value="s3">s3</option>
            <option value="avalara">avalara</option>
            <option value="mapbox">mapbox</option>
          </select>
        </div>
        <div>
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="main-payments" />
        </div>
        <div>
          <label className="label">Credentials (JSON)</label>
          <textarea className="input font-mono text-xs" rows={4} value={credentials}
            onChange={(e) => setCredentials(e.target.value)}
            placeholder={providerHints[provider]} />
          <div className="text-2xs text-ink-500 mt-1">
            Stored encrypted. Redacted in the UI after creation.
          </div>
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!name || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create integration'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Backups tab
// ============================================================
function BackupsTab() {
  const qc = useQueryClient();
  const [showRestore, setShowRestore] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['admin-backups'],
    queryFn: () => adminApi.listBackups(50),
    refetchInterval: 5000,  // poll while backups in progress
  });

  const triggerMut = useMutation({
    mutationFn: (backupType: 'full'|'schema'|'tenant_only') =>
      adminApi.triggerBackup({ backupType }),
    onSuccess: (d: any) => {
      toast.success(`Backup queued · ${d.filePath}`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['admin-backups'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kpis = (() => {
    const rows = q.data ?? [];
    let completed = 0, running = 0, failed = 0, totalBytes = 0;
    let lastCompleted: Date | null = null;
    for (const b of rows) {
      if (b.status === 'completed') {
        completed++;
        totalBytes += b.file_size_bytes ?? 0;
        if (b.completed_at) {
          const d = new Date(b.completed_at);
          if (!lastCompleted || d > lastCompleted) lastCompleted = d;
        }
      } else if (b.status === 'running' || b.status === 'queued') running++;
      else if (b.status === 'failed') failed++;
    }
    return { completed, running, failed, totalBytes, lastCompleted };
  })();

  return (
    <div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Completed" value={kpis.completed} accent={kpis.completed > 0 ? 'sage' : 'default'} />
        <KpiTile label="In progress" value={kpis.running} accent={kpis.running > 0 ? 'amber' : 'default'} />
        <KpiTile label="Failed" value={kpis.failed} accent={kpis.failed > 0 ? 'danger' : 'default'} />
        <KpiTile
          label="Last completed"
          value={kpis.lastCompleted
            ? kpis.lastCompleted.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
            : '—'}
        />
      </div>

      <div className="flex gap-2 justify-end mb-4">
        <button className="btn-secondary text-sm" disabled={triggerMut.isPending}
          onClick={() => triggerMut.mutate('schema')}>Schema only</button>
        <button className="btn-primary" disabled={triggerMut.isPending}
          onClick={() => triggerMut.mutate('full')}>Trigger full backup</button>
      </div>

      {q.isLoading ? <div className="card"><LoadingState rows={4} /></div> :
        q.error ? <ErrorState error={q.error} /> :
        !q.data?.length ? (
          <div className="card">
            <EmptyState
              message="No backups yet"
              hint="Trigger a schema-only or full backup to build your recovery history."
              action={
                <button className="btn-primary" disabled={triggerMut.isPending}
                  onClick={() => triggerMut.mutate('full')}>Trigger full backup</button>
              }
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Status</th>
                    <th className="text-right">Size</th>
                    <th>Started</th>
                    <th>Completed</th>
                    <th>Triggered by</th>
                    <th className="text-right w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data ?? []).map((b: any) => (
                    <tr key={b.id}>
                      <td className="font-mono text-xs font-semibold text-ink-900">{b.backup_type}</td>
                      <td><BackupStatusBadge status={b.status} /></td>
                      <td className="text-right font-mono text-sm font-semibold text-ink-900 tabular-nums">
                        {b.file_size_bytes ? formatBytes(b.file_size_bytes) : <span className="text-ink-400 font-normal">—</span>}
                      </td>
                      <td className="text-2xs text-ink-500 font-mono tabular-nums">
                        {b.started_at
                          ? new Date(b.started_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                          : <span className="text-ink-400">—</span>}
                      </td>
                      <td className="text-2xs text-ink-500 font-mono tabular-nums">
                        {b.completed_at
                          ? new Date(b.completed_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                          : <span className="text-ink-400">—</span>}
                      </td>
                      <td className="text-sm text-ink-700">{b.triggered_by_name ?? <span className="text-ink-400">—</span>}</td>
                      <td className="text-right">
                        {b.status === 'completed' && (
                          <button
                            className="text-xs font-semibold text-sage-700 hover:bg-sage-50 px-2 py-1 rounded transition-colors"
                            onClick={() => setShowRestore(b.id)}
                          >
                            Restore…
                          </button>
                        )}
                        {b.error_message && (
                          <span
                            className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-red-50 text-red-700 ring-red-200"
                            title={b.error_message}
                          >
                            err
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {showRestore && <RestoreModal backupId={showRestore} onClose={() => setShowRestore(null)} />}
    </div>
  );
}

function BackupStatusBadge({ status }: { status: string }) {
  const cls = {
    queued:    'bg-canvas-subtle text-ink-700 ring-ink-200',
    running:   'bg-blue-50 text-blue-700 ring-blue-200',
    completed: 'bg-sage-50 text-sage-700 ring-sage-200',
    failed:    'bg-red-50 text-red-700 ring-red-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}

function RestoreModal({ backupId, onClose }: { backupId: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['restore-instructions', backupId],
    queryFn: () => adminApi.getRestoreInstructions(backupId),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow text-danger mb-1">Admin · Restore</div>
        <h2 className="text-xl font-bold font-display text-ink-900">Restore instructions</h2>
        <p className="text-sm text-ink-500 mt-1">
          Manual restore steps — run these against a recovery database first, never directly against production.
        </p>
      </div>

      <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0">
        <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-xs text-red-800 mb-4 leading-relaxed">
          <div className="font-semibold mb-0.5">Destructive operation</div>
          <div>
            Restore is not automated. Running these steps against a live production database is
            destructive. Consider taking a fresh backup of production first.
          </div>
        </div>

        {q.isLoading ? <LoadingState rows={4} /> :
         q.error ? <ErrorState error={q.error} /> : (
          <>
            <div className="bg-canvas-subtle rounded-xl p-3 mb-4 space-y-1 text-xs text-ink-700">
              <div>
                <span className="text-ink-500">Backup file:</span>{' '}
                <span className="font-mono font-semibold text-ink-900">{q.data.filePath}</span>
              </div>
              <div>
                <span className="text-ink-500">Size:</span>{' '}
                <span className="font-mono tabular-nums">{formatBytes(q.data.size)}</span>
              </div>
            </div>
            <pre className="bg-ink-900 text-ink-100 text-xs font-mono p-4 rounded-xl whitespace-pre-wrap leading-relaxed overflow-x-auto">
              {q.data.instructions}
            </pre>
          </>
        )}
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex justify-end flex-shrink-0">
        <button className="btn-primary" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

// ============================================================
// GDPR tab
// ============================================================
function GdprTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [processingId, setProcessingId] = useState<{ id: string; label: string } | null>(null);

  const q = useQuery({
    queryKey: ['admin-data-requests'],
    queryFn: () => adminApi.listDataRequests(),
  });

  const processMut = useMutation({
    mutationFn: (id: string) => adminApi.processDataRequest(id),
    onSuccess: (d: any) => {
      toast.success(`Processed · ${JSON.stringify(d.counts ?? {})}`, { duration: 6000 });
      qc.invalidateQueries({ queryKey: ['admin-data-requests'] });
      setProcessingId(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kpis = (() => {
    const rows = q.data ?? [];
    let pending = 0, completed = 0, failed = 0;
    for (const r of rows) {
      if (r.status === 'pending' || r.status === 'processing') pending++;
      else if (r.status === 'completed') completed++;
      else if (r.status === 'failed') failed++;
    }
    return { total: rows.length, pending, completed, failed };
  })();

  return (
    <div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="All requests" value={kpis.total} />
        <KpiTile label="Pending" value={kpis.pending} accent={kpis.pending > 0 ? 'amber' : 'default'} />
        <KpiTile label="Completed" value={kpis.completed} accent={kpis.completed > 0 ? 'sage' : 'default'} />
        <KpiTile label="Failed" value={kpis.failed} accent={kpis.failed > 0 ? 'danger' : 'default'} />
      </div>

      <div className="flex justify-end mb-4">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New request</button>
      </div>

      {q.isLoading ? <div className="card"><LoadingState rows={4} /></div> :
        q.error ? <ErrorState error={q.error} /> :
        !q.data?.length ? (
          <div className="card">
            <EmptyState
              message="No data requests"
              hint="Handle GDPR-style export or deletion requests for customer data."
              action={
                <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New request</button>
              }
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Subject</th>
                    <th>Status</th>
                    <th>Requested</th>
                    <th>Completed</th>
                    <th>Records</th>
                    <th className="text-right w-28"></th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data ?? []).map((r: any) => (
                    <tr key={r.id}>
                      <td><RequestTypeBadge type={r.request_type} /></td>
                      <td>
                        <div className="text-sm text-ink-900">{r.subject_type}</div>
                        <div className="text-2xs text-ink-500 mt-0.5 font-mono">
                          {r.subject_email ?? r.subject_id.slice(0, 8)}
                        </div>
                      </td>
                      <td><GdprStatusBadge status={r.status} /></td>
                      <td className="text-2xs text-ink-500 font-mono tabular-nums">
                        {new Date(r.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </td>
                      <td className="text-2xs text-ink-500 font-mono tabular-nums">
                        {r.completed_at
                          ? new Date(r.completed_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                          : <span className="text-ink-400">—</span>}
                      </td>
                      <td className="text-2xs text-ink-600 font-mono">
                        {r.records_processed
                          ? Object.entries(r.records_processed).map(([k, v]) => `${k}:${v}`).join(', ').slice(0, 50)
                          : <span className="text-ink-400">—</span>}
                      </td>
                      <td className="text-right">
                        {r.status === 'pending' && (
                          <button
                            className="text-xs font-semibold text-sage-700 hover:bg-sage-50 px-2 py-1 rounded transition-colors"
                            disabled={processMut.isPending}
                            onClick={() => setProcessingId({
                              id: r.id,
                              label: `${r.request_type} for ${r.subject_email ?? r.subject_id.slice(0, 8)}`,
                            })}
                          >
                            Process
                          </button>
                        )}
                        {r.export_file_path && (
                          <span
                            className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-sage-50 text-sage-700 ring-sage-200"
                            title={r.export_file_path}
                          >
                            file ready
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {showCreate && <CreateGdprModal
        onClose={() => setShowCreate(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['admin-data-requests'] }); setShowCreate(false); }} />}

      {processingId && (
        <ConfirmDialog
          eyebrow="Process · GDPR"
          title="Process request?"
          body={<>Process <span className="font-mono font-semibold">{processingId.label}</span>? Export will create a file; anonymize/delete will modify records. This cannot be undone.</>}
          confirmLabel="Process"
          loading={processMut.isPending}
          onClose={() => setProcessingId(null)}
          onConfirm={() => processMut.mutate(processingId.id)}
        />
      )}
    </div>
  );
}

function GdprStatusBadge({ status }: { status: string }) {
  const cls = {
    pending:    'bg-amber-50 text-amber-800 ring-amber-200',
    processing: 'bg-blue-50 text-blue-700 ring-blue-200',
    completed:  'bg-sage-50 text-sage-700 ring-sage-200',
    failed:     'bg-red-50 text-red-700 ring-red-200',
    cancelled:  'bg-canvas-subtle text-ink-500 ring-ink-200',
  }[status] ?? 'bg-canvas-subtle text-ink-700 ring-ink-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {status}
    </span>
  );
}

function RequestTypeBadge({ type }: { type: string }) {
  const cls =
    type === 'export' ? 'bg-blue-50 text-blue-700 ring-blue-200' :
    type === 'delete_anonymize' ? 'bg-amber-50 text-amber-800 ring-amber-200' :
                                  'bg-red-50 text-red-700 ring-red-200';
  return (
    <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${cls}`}>
      {type.replace('_', ' ')}
    </span>
  );
}

function CreateGdprModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [action, setAction] = useState<'export'|'anonymize'|'hard'>('export');
  const [subjectId, setSubjectId] = useState('');
  const [reason, setReason] = useState('');

  const mut = useMutation({
    mutationFn: () => {
      if (action === 'export') return adminApi.createDataExport({ subjectId, reason: reason || undefined });
      return adminApi.createDataDeletion({
        subjectId, mode: action === 'hard' ? 'hard' : 'anonymize', reason: reason || undefined,
      });
    },
    onSuccess: () => { toast.success('Request created'); onCreated(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Admin · Compliance</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New GDPR request</h2>
        <p className="text-sm text-ink-500 mt-1">
          Log a data-subject request against a customer record. Export produces a JSON bundle; anonymize or hard delete for right-to-be-forgotten.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Action</label>
          <select className="input" value={action} onChange={(e) => setAction(e.target.value as any)}>
            <option value="export">Export customer data (JSON)</option>
            <option value="anonymize">Anonymize (keep financial records)</option>
            <option value="hard">Hard delete — destructive</option>
          </select>
        </div>
        <div>
          <label className="label">Customer ID (UUID) <span className="text-danger">*</span></label>
          <input className="input font-mono text-xs" value={subjectId}
            onChange={(e) => setSubjectId(e.target.value)}
            placeholder="e.g. 3f2a7b48-…" />
          <div className="text-2xs text-ink-500 mt-1">
            Find from Sales → Customers. Only customer subjects supported.
          </div>
        </div>
        <div>
          <label className="label">Reason <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea className="input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Recorded in the audit log" />
        </div>
        {action === 'hard' && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-xs text-red-800 leading-relaxed">
            <div className="font-semibold mb-0.5">Destructive operation</div>
            <div>
              Hard delete removes the customer record entirely. Sales orders will be orphaned
              (customer_id nulled). Not recoverable.
            </div>
          </div>
        )}
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className={`btn-primary ${action === 'hard' ? '!bg-danger hover:!bg-red-700' : ''}`}
          disabled={!subjectId || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create request'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Notifications tab
// ============================================================
function NotificationsTab() {
  const qc = useQueryClient();
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const [testingCode, setTestingCode] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['notification-templates'],
    queryFn: () => adminApi.listNotificationTemplates(),
  });

  const kpis = (() => {
    const rows = q.data ?? [];
    let active = 0, system = 0, channels = new Set<string>();
    for (const t of rows) {
      if (t.is_active) active++;
      if (t.is_system) system++;
      if (t.channel) channels.add(t.channel);
    }
    return { total: rows.length, active, system, channels: channels.size };
  })();

  return (
    <div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Templates" value={kpis.total} />
        <KpiTile label="Active" value={kpis.active} accent={kpis.active > 0 ? 'sage' : 'default'} />
        <KpiTile label="System" value={kpis.system} />
        <KpiTile label="Channels" value={kpis.channels} />
      </div>

      {q.isLoading ? <div className="card"><LoadingState rows={4} /></div> :
        q.error ? <ErrorState error={q.error} /> :
        !q.data?.length ? (
          <div className="card">
            <EmptyState
              message="No templates"
              hint="System templates should be seeded from migrations. Check backend setup."
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Channel</th>
                    <th>Subject</th>
                    <th>Tags</th>
                    <th>Active</th>
                    <th className="text-right w-28"></th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data ?? []).map((t: any) => (
                    <tr key={t.id}>
                      <td className="font-mono text-xs font-semibold text-ink-900">{t.code}</td>
                      <td>
                        <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-canvas-subtle text-ink-700 ring-ink-200">
                          {t.channel}
                        </span>
                      </td>
                      <td className="text-sm text-ink-700 truncate max-w-sm">{t.subject}</td>
                      <td>
                        {t.is_system && (
                          <span className="inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset bg-canvas-subtle text-ink-700 ring-ink-200">
                            system
                          </span>
                        )}
                      </td>
                      <td>
                        {t.is_active
                          ? <span className="text-sage-700 font-semibold">✓</span>
                          : <span className="text-ink-400">—</span>}
                      </td>
                      <td className="text-right">
                        <div className="flex gap-1 justify-end">
                          <button
                            className="text-xs font-semibold text-sage-700 hover:bg-sage-50 px-2 py-1 rounded transition-colors"
                            onClick={() => setEditingCode(t.code)}
                          >
                            Edit
                          </button>
                          <button
                            className="text-xs font-medium text-ink-500 hover:bg-canvas-subtle px-2 py-1 rounded transition-colors"
                            onClick={() => setTestingCode(t.code)}
                          >
                            Test
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {editingCode && <EditTemplateModal code={editingCode}
        onClose={() => setEditingCode(null)}
        onSaved={() => { qc.invalidateQueries({ queryKey: ['notification-templates'] }); }} />}
      {testingCode && <TestTemplateModal code={testingCode} onClose={() => setTestingCode(null)} />}
    </div>
  );
}

function EditTemplateModal({ code, onClose, onSaved }:
    { code: string; onClose: () => void; onSaved: () => void }) {
  const q = useQuery({
    queryKey: ['notification-template', code],
    queryFn: () => adminApi.getNotificationTemplate(code),
  });

  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [loaded, setLoaded] = useState(false);

  if (q.data && !loaded) {
    setSubject(q.data.subject ?? '');
    setBodyHtml(q.data.body_html ?? '');
    setBodyText(q.data.body_text ?? '');
    setLoaded(true);
  }

  const mut = useMutation({
    mutationFn: () => adminApi.updateNotificationTemplate(code, 'email',
      { subject, bodyHtml, bodyText }),
    onSuccess: () => { toast.success('Saved'); onSaved(); onClose(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (q.isLoading) return null;

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0">
        <div className="eyebrow mb-1">Admin · Template</div>
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-xl font-bold font-display text-ink-900">Edit template</h2>
          <span className="font-mono text-xs font-semibold text-ink-700">{code}</span>
        </div>
        <p className="text-sm text-ink-500 mt-1">
          Edit the email template. Use <span className="font-mono text-ink-700">{'{{variable_name}}'}</span> syntax for merge fields.
        </p>
      </div>

      <div className="px-6 py-5 overflow-y-auto flex-1 min-h-0 space-y-4">
        <div>
          <label className="label">Subject</label>
          <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder="Welcome to {{company_name}}" />
        </div>
        <div>
          <label className="label">HTML body</label>
          <textarea className="input font-mono text-xs" rows={10} value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)} />
        </div>
        <div>
          <label className="label">Plain text body</label>
          <textarea className="input font-mono text-xs" rows={4} value={bodyText}
            onChange={(e) => setBodyText(e.target.value)} />
        </div>
        <div className="text-2xs text-ink-500 leading-relaxed">
          Known merge variables are documented in the notification service source. Mismatched
          placeholders render as literal text.
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end flex-shrink-0">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Saving…' : 'Save template'}
        </button>
      </div>
    </Modal>
  );
}

function TestTemplateModal({ code, onClose }: { code: string; onClose: () => void }) {
  const [recipient, setRecipient] = useState('');
  const [mergeJson, setMergeJson] = useState('{"name":"Test User"}');

  const mut = useMutation({
    mutationFn: () => {
      let merge;
      try { merge = JSON.parse(mergeJson); } catch { throw new Error('Merge values must be valid JSON'); }
      return adminApi.testNotification({
        templateCode: code, channel: 'email', recipient, mergeValues: merge,
      });
    },
    onSuccess: (d: any) => {
      toast.success(`${d.success ? 'Sent' : 'Failed'} · ${d.previewSubject}`, { duration: 6000 });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Admin · Test</div>
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="text-xl font-bold font-display text-ink-900">Test template</h2>
          <span className="font-mono text-xs font-semibold text-ink-700">{code}</span>
        </div>
        <p className="text-sm text-ink-500 mt-1">
          Send a preview email so you can verify merge fields and formatting.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Recipient <span className="text-danger">*</span></label>
          <input type="email" className="input" value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            placeholder="you@example.com" />
        </div>
        <div>
          <label className="label">Merge values (JSON)</label>
          <textarea className="input font-mono text-xs" rows={4} value={mergeJson}
            onChange={(e) => setMergeJson(e.target.value)} />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!recipient || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Sending…' : 'Send test'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// Feature flags tab
// ============================================================
function FlagsTab() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);

  const q = useQuery({
    queryKey: ['feature-flags'],
    queryFn: () => adminApi.listFeatureFlags(),
  });

  const toggleMut = useMutation({
    mutationFn: ({ code, isEnabled }: any) => adminApi.toggleFeatureFlag(code, { isEnabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feature-flags'] }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const rolloutMut = useMutation({
    mutationFn: ({ code, rolloutPercentage }: any) => adminApi.toggleFeatureFlag(code, { rolloutPercentage }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feature-flags'] }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kpis = (() => {
    const rows = q.data ?? [];
    let enabled = 0, partial = 0;
    for (const f of rows) {
      if (f.is_enabled) {
        enabled++;
        if (f.rollout_percentage < 100) partial++;
      }
    }
    return { total: rows.length, enabled, partial };
  })();

  return (
    <div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <KpiTile label="Flags" value={kpis.total} />
        <KpiTile label="Enabled" value={kpis.enabled} accent={kpis.enabled > 0 ? 'sage' : 'default'} />
        <KpiTile label="Partial rollout" value={kpis.partial}
          accent={kpis.partial > 0 ? 'amber' : 'default'} />
      </div>

      <div className="flex justify-end mb-4">
        <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New flag</button>
      </div>

      {q.isLoading ? <div className="card"><LoadingState rows={4} /></div> :
        q.error ? <ErrorState error={q.error} /> :
        !q.data?.length ? (
          <div className="card">
            <EmptyState
              message="No feature flags defined"
              hint="Create a flag to gate a feature behind a toggle or rollout percentage."
              action={
                <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New flag</button>
              }
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Enabled</th>
                    <th>Rollout</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data ?? []).map((f: any) => (
                    <tr key={f.id}>
                      <td className="font-mono text-xs font-semibold text-ink-900">{f.code}</td>
                      <td>
                        <div className="text-sm font-medium text-ink-900">{f.name}</div>
                        {f.description && (
                          <div className="text-2xs text-ink-500 mt-0.5">{f.description}</div>
                        )}
                      </td>
                      <td>
                        <ToggleSwitch
                          checked={f.is_enabled}
                          onChange={(v) => toggleMut.mutate({ code: f.code, isEnabled: v })}
                        />
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            className="input !w-20 text-xs tabular-nums"
                            value={f.rollout_percentage}
                            onChange={(e) => rolloutMut.mutate({ code: f.code, rolloutPercentage: parseInt(e.target.value) || 0 })}
                          />
                          <span className="text-xs text-ink-500">%</span>
                        </div>
                      </td>
                      <td className="text-2xs text-ink-500 font-mono tabular-nums">
                        {f.updated_by_name && <span>{f.updated_by_name} · </span>}
                        {new Date(f.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {showCreate && <CreateFlagModal
        onClose={() => setShowCreate(false)}
        onCreated={() => { qc.invalidateQueries({ queryKey: ['feature-flags'] }); setShowCreate(false); }} />}
    </div>
  );
}

function CreateFlagModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isEnabled, setIsEnabled] = useState(false);
  const [rolloutPercentage, setRolloutPercentage] = useState(100);

  const mut = useMutation({
    mutationFn: () => adminApi.createFeatureFlag({
      code, name, description: description || undefined, isEnabled, rolloutPercentage,
    }),
    onSuccess: () => { toast.success('Flag created'); onCreated(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Admin · Feature flag</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New feature flag</h2>
        <p className="text-sm text-ink-500 mt-1">
          Create a flag to gate a feature rollout. Rollout % controls the percentage of traffic that sees the new behaviour once enabled.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Code <span className="text-danger">*</span></label>
          <input className="input font-mono" value={code}
            onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9_\-.]/g, ''))}
            placeholder="new_checkout_flow" />
          <div className="text-2xs text-ink-500 mt-1">
            Lowercase, alphanumeric, with <span className="font-mono">_</span>
            <span className="mx-1">·</span>
            <span className="font-mono">-</span>
            <span className="mx-1">·</span>
            <span className="font-mono">.</span> allowed.
          </div>
        </div>
        <div>
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="New checkout flow" />
        </div>
        <div>
          <label className="label">Description <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <textarea className="input" rows={2} value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What the flag gates and when it should be retired" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-sm text-ink-700 cursor-pointer">
              <input type="checkbox" checked={isEnabled} onChange={(e) => setIsEnabled(e.target.checked)} />
              Enabled
            </label>
          </div>
          <div>
            <label className="label">Rollout %</label>
            <input type="number" min={0} max={100}
              className="input tabular-nums" value={rolloutPercentage}
              onChange={(e) => setRolloutPercentage(parseInt(e.target.value) || 0)} />
          </div>
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!code || !name || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create flag'}
        </button>
      </div>
    </Modal>
  );
}

// ============================================================
// API keys + webhooks tab
// ============================================================
function KeysWebhooksTab() {
  return (
    <div className="space-y-8">
      <ApiKeysSection />
      <WebhooksSection />
    </div>
  );
}

function ApiKeysSection() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newKey, setNewKey] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<{ id: string; label: string } | null>(null);

  const q = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => adminApi.listApiKeys(),
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => adminApi.revokeApiKey(id),
    onSuccess: () => {
      toast.success('Revoked');
      qc.invalidateQueries({ queryKey: ['api-keys'] });
      setRevokingId(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div>
      <SectionHeader
        eyebrow="Authentication"
        title="API keys"
        caveat="API key auth middleware stores keys securely but does not currently validate them on inbound requests. Wire middleware before exposing API keys to production."
        action={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New API key</button>
        }
      />

      {newKey && (
        <div className="bg-sage-50 border border-sage-200 rounded-xl p-4 mb-4">
          <div className="eyebrow text-sage-700 mb-1.5">API key created</div>
          <div className="text-sm text-sage-800 mb-2">
            Save now — this is the only time the full key will be displayed.
          </div>
          <code className="bg-canvas-elevated p-3 block rounded-lg font-mono text-xs break-all border border-sage-200">
            {newKey}
          </code>
          <button
            className="text-xs font-semibold text-sage-700 mt-3 hover:text-sage-800 transition-colors"
            onClick={() => setNewKey(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {q.isLoading ? <div className="card"><LoadingState rows={3} /></div> :
        q.error ? <ErrorState error={q.error} /> :
        !q.data?.length ? (
          <div className="card">
            <EmptyState
              message="No API keys"
              hint="Create a key to give an external service scoped access to the API."
              action={
                <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New API key</button>
              }
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Prefix</th>
                    <th>Scopes</th>
                    <th>Status</th>
                    <th className="text-right">Used</th>
                    <th>Last used</th>
                    <th className="text-right w-24"></th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data ?? []).map((k: any) => (
                    <tr key={k.id}>
                      <td className="text-sm font-medium text-ink-900">{k.name}</td>
                      <td className="font-mono text-xs text-ink-700">pk_live_{k.key_prefix}…</td>
                      <td className="text-2xs text-ink-600">
                        {Array.isArray(k.scopes)
                          ? k.scopes.slice(0, 3).join(', ') + (k.scopes.length > 3 ? ` +${k.scopes.length - 3}` : '')
                          : <span className="text-ink-400">—</span>}
                      </td>
                      <td>
                        <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${
                          k.status === 'active'
                            ? 'bg-sage-50 text-sage-700 ring-sage-200'
                            : 'bg-canvas-subtle text-ink-500 ring-ink-200'
                        }`}>
                          {k.status}
                        </span>
                      </td>
                      <td className="text-right font-mono font-semibold text-ink-900 tabular-nums text-sm">
                        {k.use_count}
                      </td>
                      <td className="text-2xs text-ink-500 font-mono tabular-nums">
                        {k.last_used_at
                          ? new Date(k.last_used_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                          : <span className="text-ink-400">—</span>}
                      </td>
                      <td className="text-right">
                        {k.status === 'active' && (
                          <button
                            className="text-xs font-semibold text-danger hover:bg-red-50 px-2 py-1 rounded transition-colors"
                            onClick={() => setRevokingId({ id: k.id, label: k.name })}
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {showCreate && <CreateApiKeyModal
        onClose={() => setShowCreate(false)}
        onCreated={(key) => {
          setNewKey(key);
          qc.invalidateQueries({ queryKey: ['api-keys'] });
          setShowCreate(false);
        }} />}

      {revokingId && (
        <ConfirmDialog
          eyebrow="Revoke · API key"
          title="Revoke API key?"
          body={<>Revoke <span className="font-mono font-semibold">{revokingId.label}</span>? The key will stop working immediately and cannot be reactivated. A new key would need to be issued.</>}
          confirmLabel="Revoke"
          loading={revokeMut.isPending}
          onClose={() => setRevokingId(null)}
          onConfirm={() => revokeMut.mutate(revokingId.id)}
        />
      )}
    </div>
  );
}

function CreateApiKeyModal({ onClose, onCreated }:
    { onClose: () => void; onCreated: (key: string) => void }) {
  const [name, setName] = useState('');
  const [scopesText, setScopesText] = useState('read:products\nread:orders');
  const [expiresAt, setExpiresAt] = useState('');

  const mut = useMutation({
    mutationFn: () => {
      const scopes = scopesText.split('\n').map((s) => s.trim()).filter(Boolean);
      return adminApi.createApiKey({
        name, scopes, expiresAt: expiresAt || undefined,
      });
    },
    onSuccess: (d: any) => onCreated(d.apiKey),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Admin · API key</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New API key</h2>
        <p className="text-sm text-ink-500 mt-1">
          Scopes are shown to you once on creation — copy the key somewhere safe before closing.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Mobile app integration" />
        </div>
        <div>
          <label className="label">Scopes <span className="text-danger">*</span> <span className="font-normal text-ink-400 normal-case tracking-normal">(one per line)</span></label>
          <textarea className="input font-mono text-xs" rows={5} value={scopesText}
            onChange={(e) => setScopesText(e.target.value)} />
          <div className="text-2xs text-ink-500 mt-1 font-mono">
            read:products · read:orders · write:products · admin:*
          </div>
        </div>
        <div>
          <label className="label">Expires at <span className="font-normal text-ink-400 normal-case tracking-normal">(optional)</span></label>
          <input type="datetime-local" className="input" value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)} />
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!name || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create key'}
        </button>
      </div>
    </Modal>
  );
}

function WebhooksSection() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<{ id: string; label: string } | null>(null);

  const q = useQuery({
    queryKey: ['webhooks'],
    queryFn: () => adminApi.listWebhooks(),
  });

  const testMut = useMutation({
    mutationFn: (id: string) => adminApi.testWebhook(id),
    onSuccess: (d: any) => toast[d.success ? 'success' : 'error'](
      d.success ? `Test delivered · HTTP ${d.status} after ${d.attempts} attempt(s)`
                : `Test failed after ${d.attempts} attempts: ${d.error}`,
      { duration: 7000 }
    ),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => adminApi.deleteWebhook(id),
    onSuccess: () => {
      toast.success('Deleted');
      qc.invalidateQueries({ queryKey: ['webhooks'] });
      setDeletingId(null);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div>
      <SectionHeader
        eyebrow="Outbound events"
        title="Webhooks"
        caveat="Webhook endpoints exist but domain event hooks are not currently called from service code. Wire `eventDispatcher.emit(...)` calls into business logic to start firing webhooks."
        action={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New webhook</button>
        }
      />

      {newSecret && (
        <div className="bg-sage-50 border border-sage-200 rounded-xl p-4 mb-4">
          <div className="eyebrow text-sage-700 mb-1.5">Webhook created</div>
          <div className="text-sm text-sage-800 mb-2">Signing secret — use this to verify HMAC-SHA256 signatures on incoming events.</div>
          <code className="bg-canvas-elevated p-3 block rounded-lg font-mono text-xs break-all border border-sage-200">
            {newSecret}
          </code>
          <button
            className="text-xs font-semibold text-sage-700 mt-3 hover:text-sage-800 transition-colors"
            onClick={() => setNewSecret(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {q.isLoading ? <div className="card"><LoadingState rows={3} /></div> :
        q.error ? <ErrorState error={q.error} /> :
        !q.data?.length ? (
          <div className="card">
            <EmptyState
              message="No webhooks"
              hint="Register a URL to receive real-time events like order.created or invoice.paid."
              action={
                <button className="btn-primary" onClick={() => setShowCreate(true)}>+ New webhook</button>
              }
            />
          </div>
        ) : (
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>URL</th>
                    <th>Events</th>
                    <th>Status</th>
                    <th className="text-right">Success</th>
                    <th className="text-right">Failures</th>
                    <th className="text-right w-36"></th>
                  </tr>
                </thead>
                <tbody>
                  {(q.data ?? []).map((w: any) => (
                    <tr key={w.id}>
                      <td className="text-sm font-medium text-ink-900">{w.name}</td>
                      <td className="font-mono text-2xs text-ink-700 truncate max-w-xs" title={w.url}>
                        {w.url}
                      </td>
                      <td className="text-2xs text-ink-600">
                        {Array.isArray(w.events)
                          ? w.events.slice(0, 2).join(', ') + (w.events.length > 2 ? ` +${w.events.length - 2}` : '')
                          : <span className="text-ink-400">—</span>}
                      </td>
                      <td>
                        <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${
                          w.status === 'active'
                            ? 'bg-sage-50 text-sage-700 ring-sage-200'
                            : 'bg-canvas-subtle text-ink-500 ring-ink-200'
                        }`}>
                          {w.status}
                        </span>
                      </td>
                      <td className="text-right font-mono text-sm text-sage-700 tabular-nums font-medium">
                        {w.success_count}
                      </td>
                      <td className={`text-right font-mono text-sm tabular-nums font-medium ${
                        w.failure_count > 0 ? 'text-danger' : 'text-ink-400'
                      }`}>
                        {w.failure_count}
                      </td>
                      <td className="text-right">
                        <div className="flex gap-1 justify-end">
                          <button
                            className="text-xs font-semibold text-sage-700 hover:bg-sage-50 px-2 py-1 rounded transition-colors"
                            disabled={testMut.isPending}
                            onClick={() => testMut.mutate(w.id)}
                          >
                            Test
                          </button>
                          <button
                            className="text-xs font-medium text-ink-500 hover:bg-canvas-subtle px-2 py-1 rounded transition-colors"
                            onClick={() => setSelected(w.id)}
                          >
                            Deliveries
                          </button>
                          <button
                            className="text-xs font-semibold text-danger hover:bg-red-50 px-2 py-1 rounded transition-colors"
                            onClick={() => setDeletingId({ id: w.id, label: w.name })}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      {showCreate && <CreateWebhookModal
        onClose={() => setShowCreate(false)}
        onCreated={(secret) => {
          setNewSecret(secret);
          qc.invalidateQueries({ queryKey: ['webhooks'] });
          setShowCreate(false);
        }} />}

      {selected && <DeliveriesModal webhookId={selected} onClose={() => setSelected(null)} />}

      {deletingId && (
        <ConfirmDialog
          eyebrow="Delete · Webhook"
          title="Delete webhook?"
          body={<>Delete <span className="font-mono font-semibold">{deletingId.label}</span>? Future events will no longer be delivered to this endpoint.</>}
          confirmLabel="Delete"
          loading={deleteMut.isPending}
          onClose={() => setDeletingId(null)}
          onConfirm={() => deleteMut.mutate(deletingId.id)}
        />
      )}
    </div>
  );
}

function CreateWebhookModal({ onClose, onCreated }:
    { onClose: () => void; onCreated: (secret: string) => void }) {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [eventsText, setEventsText] = useState('order.created\ninvoice.paid');
  const [retryCount, setRetryCount] = useState(3);

  const mut = useMutation({
    mutationFn: () => {
      const events = eventsText.split('\n').map((s) => s.trim()).filter(Boolean);
      return adminApi.createWebhook({ name, url, events, retryCount });
    },
    onSuccess: (d: any) => onCreated(d.secret),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
      <div className="px-6 pt-6 pb-2">
        <div className="eyebrow mb-1">Admin · Webhook</div>
        <h2 className="text-xl font-bold font-display text-ink-900">New webhook</h2>
        <p className="text-sm text-ink-500 mt-1">
          The signing secret is shown once on creation — copy it before closing so you can verify payload signatures on the receiving side.
        </p>
      </div>

      <div className="px-6 py-5 space-y-4">
        <div>
          <label className="label">Name <span className="text-danger">*</span></label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Analytics pipeline" />
        </div>
        <div>
          <label className="label">URL <span className="text-danger">*</span></label>
          <input className="input font-mono text-xs" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/webhooks/omniretail" />
        </div>
        <div>
          <label className="label">Events <span className="text-danger">*</span> <span className="font-normal text-ink-400 normal-case tracking-normal">(one per line)</span></label>
          <textarea className="input font-mono text-xs" rows={5} value={eventsText}
            onChange={(e) => setEventsText(e.target.value)} />
          <div className="text-2xs text-ink-500 mt-1 leading-relaxed">
            Known: <span className="font-mono">order.created, order.shipped, order.invoiced, customer.created, invoice.paid, stock.low, cart.abandoned, review.submitted</span>
          </div>
        </div>
        <div>
          <label className="label">Retry count</label>
          <input type="number" min={0} max={10}
            className="input tabular-nums" value={retryCount}
            onChange={(e) => setRetryCount(parseInt(e.target.value) || 0)} />
          <div className="text-2xs text-ink-500 mt-1">
            0 means no retries. Exponential backoff on failures.
          </div>
        </div>
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={!name || !url || mut.isPending}
          onClick={() => mut.mutate()}>
          {mut.isPending ? 'Creating…' : 'Create webhook'}
        </button>
      </div>
    </Modal>
  );
}

function DeliveriesModal({ webhookId, onClose }: { webhookId: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['webhook-deliveries', webhookId],
    queryFn: () => adminApi.listWebhookDeliveries(webhookId),
  });

  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden">
      <div className="px-6 pt-6 pb-2 flex-shrink-0 flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow mb-1">Admin · Webhook</div>
          <h2 className="text-xl font-bold font-display text-ink-900">Recent deliveries</h2>
          <p className="text-sm text-ink-500 mt-1">Last attempts for this endpoint, newest first.</p>
        </div>
        <button
          className="w-8 h-8 flex items-center justify-center text-ink-400 hover:text-ink-900 hover:bg-canvas-subtle rounded-md transition-colors flex-shrink-0 mt-1"
          onClick={onClose}
          aria-label="Close"
        >✕</button>
      </div>

      <div className="px-6 py-4 flex-1 overflow-y-auto min-h-0">
        {q.isLoading ? <div className="card"><LoadingState rows={4} /></div> :
         q.error ? <ErrorState error={q.error} /> : (
          <div className="border border-ink-100 rounded-xl overflow-hidden">
            <table className="table text-xs">
              <thead>
                <tr>
                  <th>Event</th>
                  <th>Status</th>
                  <th className="text-right">Attempt</th>
                  <th className="text-right">Code</th>
                  <th>When</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {!q.data?.length ? (
                  <tr><td colSpan={6}><EmptyState message="No deliveries yet." /></td></tr>
                ) : (q.data ?? []).map((d: any) => (
                  <tr key={d.id}>
                    <td className="font-mono font-semibold text-ink-900">{d.event_type}</td>
                    <td>
                      <span className={`inline-flex items-center font-semibold text-2xs uppercase tracking-wider px-2 py-0.5 rounded ring-1 ring-inset ${
                        d.status === 'success'
                          ? 'bg-sage-50 text-sage-700 ring-sage-200'
                          : 'bg-red-50 text-red-700 ring-red-200'
                      }`}>{d.status}</span>
                    </td>
                    <td className="text-right font-mono tabular-nums">{d.attempt}</td>
                    <td className="text-right font-mono tabular-nums text-ink-700">
                      {d.response_code ?? <span className="text-ink-400">—</span>}
                    </td>
                    <td className="text-2xs text-ink-500 font-mono tabular-nums whitespace-nowrap">
                      {new Date(d.delivered_at).toLocaleString(undefined, {
                        month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td className="text-2xs text-danger truncate max-w-[180px]">
                      {d.error_message ?? <span className="text-ink-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex justify-end flex-shrink-0">
        <button className="btn-primary" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

// ============================================================
// Shared helpers
// ============================================================
function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}

function SectionHeader({
  eyebrow, title, caveat, action,
}: {
  eyebrow: string;
  title: string;
  caveat?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-4">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
        <div>
          <div className="eyebrow text-ink-500 mb-0.5">{eyebrow}</div>
          <h2 className="font-display font-bold text-xl text-ink-900">{title}</h2>
        </div>
        {action}
      </div>
      {caveat && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-900 leading-relaxed">
          {caveat}
        </div>
      )}
    </div>
  );
}

function ToggleSwitch({
  checked, onChange,
}: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        checked ? 'bg-sage-500' : 'bg-ink-200'
      }`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${
        checked ? 'translate-x-5' : 'translate-x-1'
      }`} />
    </button>
  );
}

function ConfirmDialog({
  eyebrow, title, body, confirmLabel, loading, onClose, onConfirm,
}: {
  eyebrow: string;
  title: string;
  body: React.ReactNode;
  confirmLabel: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      onClose={onClose}
      overlayClassName="fixed inset-0 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      className="bg-canvas-elevated rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
      <div className="px-6 pt-6 pb-4">
        <div className="eyebrow text-danger mb-1">{eyebrow}</div>
        <h2 className="text-xl font-bold font-display text-ink-900">{title}</h2>
        <p className="text-sm text-ink-600 mt-3 leading-relaxed">{body}</p>
      </div>
      <div className="px-6 py-4 bg-canvas-subtle border-t border-ink-100 flex gap-2 justify-end">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button
          className="btn-primary !bg-danger hover:!bg-red-700"
          disabled={loading}
          onClick={onConfirm}
        >
          {loading ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
